import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Search, Button } from 'carbon-components-react';
import { WarningFilled16, Search16 } from '@carbon/icons-react';

const parseAndValidateCoordinate = (value: string) => {
  const coordinateArr = value.split(',');
  if (
    coordinateArr.length === 2 &&
    /^-?\d+(\.\d{1,})?$/.test(coordinateArr[0].trim()) &&
    /^-?\d+(\.\d{1,})?$/.test(coordinateArr[1].trim())
  ) {
    const lat = Number(coordinateArr[0].trim());
    const lon = Number(coordinateArr[1].trim());
    let isValidCoordinate = true;

    if (
      Number.isNaN(lat) ||
      Number.isNaN(lon) ||
      lat === 0 ||
      lon === 0 ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      isValidCoordinate = false;
    }

    return { lat, lon, isValidCoordinate };
  }

  return { lat: NaN, lon: NaN, isValidCoordinate: false };
};

const LatLongSearch: React.FC<Props> = ({
  showHideBox,
  placeholder,
  noOpenButton,
  searchResult,
  onClickSearchCoordinate
}) => {
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [searchInvalid, setSearchInvalid] = useState<boolean>(false);

  const searchButtonRef = useRef<HTMLButtonElement>();

  const handleInputChange = (value: string) => {
    if (!value) {
      setSearchInvalid(false);
    }
    setSearchInputValue(value);
  };

  const searchCoordinate = (evt: Event) => {
    evt.preventDefault();
    evt.stopPropagation();

    const searchButtonElem = searchButtonRef?.current;
    searchButtonElem && searchButtonElem.blur();

    const { lat, lon, isValidCoordinate } = parseAndValidateCoordinate(
      searchInputValue
    );
    showHideBox(false, false);
    if (!isValidCoordinate) {
      setSearchInvalid(true);
    } else {
      setSearchInvalid(false);
      const center = [lon, lat];
      onClickSearchCoordinate(searchInputValue, center, `${lon},${lat}`);
    }
  };

  useEffect(() => {
    setSearchInputValue(searchResult || '');
  }, [searchResult]);

  return (
    <div className="mls-multiple-layout-search">
      <div
        className={`mls-control-container${searchInvalid ? ' invalid' : ''}`}
      >
        <Search
          id="mls-input"
          labelText=""
          size="sm"
          value={searchInputValue}
          placeHolderText={placeholder}
          onFocus={showHideBox}
          onChange={(evt) => handleInputChange(evt.target.value)}
          className={`${!noOpenButton ? 'bx--search-open-button' : ''}`}
        />
        {searchInvalid && <WarningFilled16 className="invalid-icon" />}
      </div>
      <Button
        className="mls-search-button"
        renderIcon={Search16}
        onClick={searchCoordinate}
        disabled={!searchInputValue}
        hasIconOnly
        kind="tertiary"
        iconDescription="Search coordinate"
        tooltipPosition="right"
        ref={searchButtonRef}
      />
    </div>
  );
};

const propTypes = {
  showHideBox: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  noOpenButton: PropTypes.bool,
  onClickSearchCoordinate: PropTypes.func.isRequired,
  searchResult: PropTypes.string
};

type Props = PropTypes.InferProps<typeof propTypes>;

LatLongSearch.defaultProps = {
  noOpenButton: false,
  placeholder: ''
};

export default LatLongSearch;
